<div class="view-plan-page">
  <!-- Header with Back Button and Actions -->
  <div class="page-header">
    <div class="header-left">
      <button type="button" class="back-button" (click)="goBack()" title="Back">
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
        <span>Back</span>
      </button>

      @if (!isLoading && selectedPlan) {
        <div class="title-section">
          <div class="title-row">
            <h1 class="page-title">{{ selectedPlan.display_name }}</h1>
          </div>
          <div class="plan-slug">
            System Identifier: <code>{{ selectedPlan.plan_name }}</code>
          </div>
        </div>
      } @else if (isLoading) {
        <div class="skeleton-header-title"></div>
      }
    </div>

    @if (!isLoading && selectedPlan) {
      <div class="header-actions">
        <span
          class="archive-btn-wrapper"
          [class.disabled]="isArchiveDisabled"
          [title]="archiveButtonTitle"
        >
          <button
            type="button"
            class="btn-secondary"
            [disabled]="isArchiveDisabled"
            [title]="archiveButtonTitle"
            (click)="openArchiveConfirm()"
          >
            {{ selectedPlan.is_active ? "Archive Plan" : "Unarchive Plan" }}
          </button>
        </span>

        <button
          type="button"
          class="btn-primary"
          [disabled]="!selectedPlan.is_active"
          (click)="onEditPlan()"
          [title]="
            selectedPlan.is_active ? 'Edit Plan' : 'Cannot edit archived plan'
          "
        >
          Edit Plan
        </button>
      </div>
    }
  </div>

  <!-- Loading State -->
  @if (isLoading) {
    <div class="skeleton-view-grid">
      <div class="skeleton-card large"></div>
      <div class="skeleton-card medium"></div>
      <div class="skeleton-card small"></div>
    </div>
  } @else if (selectedPlan) {
    <div class="view-content-grid">
      <!-- Section 1: Overview -->
      <section class="details-card">
        <div class="card-header">
          <h2 class="card-title">Plan Overview</h2>
        </div>

        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">Display Name</span>
            <span class="info-value strong">{{
              selectedPlan.display_name
            }}</span>
          </div>

          <div class="info-item">
            <span class="info-label">System Slug</span>
            <span class="info-value font-mono">{{
              selectedPlan.plan_name
            }}</span>
          </div>

          <div class="info-item">
            <span class="info-label">Status</span>
            <span class="info-value">
              <span
                class="status-pill"
                [class.active]="selectedPlan.is_active"
                [class.inactive]="!selectedPlan.is_active"
              >
                {{ selectedPlan.is_active ? "Active" : "Archived" }}
              </span>
            </span>
          </div>

          <div class="info-item">
            <span class="info-label">Trial Period</span>
            <span class="info-value">
              {{
                selectedPlan.trial_period_days
                  ? selectedPlan.trial_period_days + " days"
                  : "No trial"
              }}
            </span>
          </div>

          <div class="info-item">
            <span class="info-label">Created</span>
            <span class="info-value">
              {{ selectedPlan.created_at | date: "MMM d, y, h:mm a" }}
            </span>
          </div>

          <div class="info-item">
            <span class="info-label">Last Updated</span>
            <span class="info-value">
              {{ selectedPlan.updated_at | date: "MMM d, y, h:mm a" }}
            </span>
          </div>
        </div>
      </section>

      <!-- Section 2: Pricing Schedule -->
      <section class="details-card">
        <div class="card-header">
          <h2 class="card-title">Pricing</h2>
        </div>

        <div class="table-responsive">
          <table class="card-table">
            <thead>
              <tr>
                <th>Billing Interval</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              @for (price of selectedPlan.prices; track price.id) {
                <tr>
                  <td>{{ price.billing_interval | titlecase }}</td>
                  <td>
                    <strong>
                      {{ price.amount | currency: "USD" : "symbol" : "1.2-2" }}
                    </strong>
                  </td>
                </tr>
              } @empty {
                <tr>
                  <td colspan="2" class="empty-cell">No pricing available.</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </section>

      <!-- Section 3: Features -->
      <section class="details-card">
        <div class="card-header">
          <h2 class="card-title">Features</h2>
        </div>

        <div class="chip-container">
          @for (feature of planFeatures; track feature) {
            <span class="feature-chip">{{ feature }}</span>
          } @empty {
            <span class="empty-text">No features assigned to this plan.</span>
          }
        </div>
      </section>

      <!-- Section 4: Connections Assigned -->
      <section class="details-card">
        <div class="card-header">
          <div>
            <h2 class="card-title">Connections on this Plan</h2>
            <p class="section-note">
              Eligible Organizations assigned to this plan. Filter to subscribed
              connections or ones that can start a trial. Open Stripe to change
              billing.
            </p>
          </div>
        </div>

        <!-- Filter Tabs -->
        <div class="connection-filter-tabs">
          <button
            type="button"
            class="filter-tab"
            [class.selected]="planConnectionFilter === 'all'"
            (click)="setPlanConnectionFilter('all')"
          >
            All ({{ getPlanConnectionTabCounts().all }})
          </button>
          <button
            type="button"
            class="filter-tab"
            [class.selected]="planConnectionFilter === 'subscribed'"
            (click)="setPlanConnectionFilter('subscribed')"
          >
            Subscribed ({{ getPlanConnectionTabCounts().subscribed }})
          </button>
          <button
            type="button"
            class="filter-tab"
            [class.selected]="planConnectionFilter === 'trial'"
            (click)="setPlanConnectionFilter('trial')"
          >
            Can start trial ({{ getPlanConnectionTabCounts().trial }})
          </button>
          <button
            type="button"
            class="filter-tab"
            [class.selected]="planConnectionFilter === 'not_subscribed'"
            (click)="setPlanConnectionFilter('not_subscribed')"
          >
            Not subscribed ({{ getPlanConnectionTabCounts().not_subscribed }})
          </button>
        </div>

        <!-- Connections Table -->
        <div class="table-responsive">
          <table class="card-table">
            <thead>
              <tr>
                <th>Connection</th>
                <th>Subscription Admins</th>
                <th>Status</th>
                <th>Period End</th>
                <th>Stripe</th>
                @if (canShowTrialActions()) {
                  <th style="text-align: right">Actions</th>
                }
              </tr>
            </thead>
            <tbody>
              @for (
                row of getFilteredPlanConnectionRows();
                track row.connection.connection_name ||
                  row.connection.connection_id
              ) {
                <tr>
                  <td>
                    <div>
                      <strong>
                        {{
                          row.connection.display_name ||
                            row.connection.connection_name
                        }}
                      </strong>
                      @if (
                        row.connection.display_name &&
                        row.connection.display_name !==
                          row.connection.connection_name
                      ) {
                        <div class="conn-system-subtext">
                          {{ row.connection.connection_name }}
                        </div>
                      }
                    </div>
                  </td>
                  <td>
                    @let rowAdmins = getAdminsForRow(row);
                    @if (rowAdmins.length === 0) {
                      <span class="empty-cell-text">No admin</span>
                    } @else if (rowAdmins.length === 1) {
                      <div
                        class="admin-single"
                        [title]="
                          rowAdmins[0].email
                            ? rowAdmins[0].name +
                              ' (' +
                              rowAdmins[0].email +
                              ')'
                            : rowAdmins[0].name
                        "
                      >
                        <div class="admin-name">{{ rowAdmins[0].name }}</div>
                        @if (rowAdmins[0].email) {
                          <div class="admin-email">
                            {{ rowAdmins[0].email }}
                          </div>
                        }
                      </div>
                    } @else {
                      <div class="admin-badges-list">
                        @for (
                          admin of getVisibleAdmins(row);
                          track admin.id || admin.name
                        ) {
                          <span
                            class="admin-badge"
                            [title]="
                              admin.email
                                ? admin.name + ' (' + admin.email + ')'
                                : admin.name
                            "
                          >
                            <svg
                              class="admin-badge-icon"
                              width="11"
                              height="11"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            >
                              <path
                                d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
                              />
                              <circle cx="12" cy="7" r="4" />
                            </svg>
                            <span class="badge-text">{{ admin.name }}</span>
                          </span>
                        }
                        @if (getHiddenAdminsCount(row) > 0) {
                          <button
                            type="button"
                            class="admin-badge more"
                            [title]="
                              'View all ' +
                              rowAdmins.length +
                              ' admins: ' +
                              getAdminsTooltip(row)
                            "
                            (click)="openViewAdmins(row)"
                          >
                            +{{ getHiddenAdminsCount(row) }} more
                          </button>
                        }
                      </div>
                    }
                  </td>
                  <td>
                    @if (
                      row.statusLabel === "other_plan" &&
                      row.connection.active_plan_name
                    ) {
                      <a
                        [routerLink]="[
                          '/configs/manage-subscription-plans/view',
                          row.connection.active_plan_name,
                        ]"
                        (click)="
                          navigateToPlan(
                            row.connection.active_plan_name,
                            $event
                          )
                        "
                        class="status-pill other-plan clickable"
                        [title]="
                          'View details for ' +
                          row.connection.active_plan_name +
                          ' plan'
                        "
                      >
                        <span>On {{ row.connection.active_plan_name }}</span>
                        <svg
                          class="pill-link-icon"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        >
                          <path
                            d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3"
                          />
                        </svg>
                      </a>
                    } @else {
                      <span
                        class="status-pill"
                        [class.active]="row.statusLabel === 'active'"
                        [class.trialing]="row.statusLabel === 'trialing'"
                        [class.other-plan]="row.statusLabel === 'other_plan'"
                        [class.not-subscribed]="
                          row.statusLabel === 'not_subscribed'
                        "
                      >
                        {{ getConnectionStatusText(row) }}
                      </span>
                    }
                  </td>
                  <td>
                    {{
                      getSubscriptionPeriodEnd(row)
                        ? (getSubscriptionPeriodEnd(row) | date: "MMM d, y")
                        : "—"
                    }}
                  </td>
                  <td>
                    @if (
                      row.subscriptionOnPlan?.stripe_customer_dashboard_url
                    ) {
                      <a
                        class="stripe-portal-link"
                        [href]="
                          row.subscriptionOnPlan?.stripe_customer_dashboard_url
                        "
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open customer in Stripe Dashboard"
                      >
                        <span>View in Stripe</span>
                        <svg
                          class="external-link-icon"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        >
                          <path
                            d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3"
                          />
                        </svg>
                      </a>
                    } @else {
                      —
                    }
                  </td>
                  @if (canShowTrialActions()) {
                    <td style="text-align: right">
                      <button
                        type="button"
                        class="table-action-btn"
                        [disabled]="
                          !row.canStartTrial ||
                          startingTrialConnectionName ===
                            row.connection.connection_name
                        "
                        [title]="getTrialActionTitle(row)"
                        (click)="openTrialConfirm(row.connection)"
                      >
                        Start trial
                      </button>
                    </td>
                  }
                </tr>
              } @empty {
                <tr>
                  <td
                    [attr.colspan]="canShowTrialActions() ? 6 : 5"
                    class="empty-cell"
                  >
                    {{ getPlanConnectionEmptyMessage() }}
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </section>
    </div>
  }
</div>

<!-- Confirm Archive/Unarchive Popup -->
@if (showConfirmPopup && selectedPlan) {
  <app-popup
    [heading]="selectedPlan.is_active ? 'Confirm Archive' : 'Confirm Unarchive'"
    [confirmText]="
      isArchiving
        ? selectedPlan.is_active
          ? 'Archiving...'
          : 'Unarchiving...'
        : selectedPlan.is_active
          ? 'Archive'
          : 'Unarchive'
    "
    [cancelText]="'Cancel'"
    [isConfirmDisabled]="isArchiving"
    (confirmClicked)="onConfirmArchive()"
    (cancelClicked)="closeArchiveConfirm()"
  >
    <div class="archive-confirm-content">
      <p>
        Are you sure you want to
        <strong>{{ selectedPlan.is_active ? "archive" : "unarchive" }}</strong>
        the plan <strong>"{{ selectedPlan.display_name }}"</strong>?
      </p>
      @if (selectedPlan.is_active) {
        <p class="warning-subtext">
          Archived plans will not be available for new subscriptions. Existing
          active subscriptions will remain unaffected.
        </p>
      }
    </div>
  </app-popup>
}

<!-- Confirm Start Trial Popup -->
@if (showTrialConfirmPopup && trialConfirmConnection && selectedPlan) {
  <app-popup
    heading="Confirm Start Trial"
    [confirmText]="startingTrialConnectionName ? 'Starting...' : 'Start Trial'"
    cancelText="Cancel"
    [isConfirmDisabled]="!!startingTrialConnectionName"
    (confirmClicked)="onConfirmStartTrial()"
    (cancelClicked)="closeTrialConfirm()"
  >
    <div class="trial-confirm-content">
      <p>
        Are you sure you want to start a
        <strong>{{ selectedPlan.trial_period_days }}-day trial</strong>
        of <strong>{{ selectedPlan.display_name }}</strong> for
        <strong>{{
          trialConfirmConnection.display_name ||
            trialConfirmConnection.connection_name
        }}</strong
        >?
      </p>

      <div class="stripe-notice-box">
        <svg
          class="notice-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <div class="notice-text">
          <strong>Important Notice:</strong> Once active, subscription
          cancellations and billing updates must be managed directly in the
          <strong>Stripe Dashboard</strong>.
        </div>
      </div>
    </div>
  </app-popup>
}

<!-- View Admins Popup -->
@if (viewAdminsRow) {
  <app-popup
    class="view-admins-popup"
    [heading]="
      'Subscription Admins: ' +
      (viewAdminsRow.connection.display_name ||
        viewAdminsRow.connection.connection_name)
    "
    [confirmText]="'Close'"
    [cancelText]="''"
    [showCloseIcon]="true"
    (confirmClicked)="closeViewAdmins()"
    (cancelClicked)="closeViewAdmins()"
  >
    <div class="view-admins-modal-content">
      <p class="view-admins-subtitle">
        Users in this organization authorized to manage subscriptions, billing,
        and transactions.
      </p>

      <ul class="view-admins-list">
        @for (
          admin of getAdminsForRow(viewAdminsRow);
          track admin.id || admin.name
        ) {
          <li class="view-admin-item">
            <div class="admin-avatar">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>
            <div class="admin-details">
              <span class="admin-full-name">{{ admin.name }}</span>
              @if (admin.email) {
                <span class="admin-email-text">{{ admin.email }}</span>
              }
            </div>
          </li>
        }
      </ul>
    </div>
  </app-popup>
}
